/**
 * US States Select Populator
 *
 * Automatically populates a <select> element with all 50 US states.
 * Maintains the existing placeholder option and adds state options dynamically.
 *
 * Features:
 * - Auto-populates all 50 US states (full names + abbreviations)
 * - Preserves placeholder option (must have empty value)
 * - Validates select has exactly 1 template option
 * - State values use standard 2-letter abbreviations (CA, NY, TX, etc.)
 * - Works with both native and custom select components
 *
 * HTML Structure:
 *   <select data-hs-form-select="states">
 *     <option value="">Select a state</option>
 *   </select>
 *
 * Result:
 *   <select data-hs-form-select="states">
 *     <option value="">Select a state</option>
 *     <option value="AL">Alabama</option>
 *     <option value="AK">Alaska</option>
 *     ...all 50 states...
 *   </select>
 *
 * Validation:
 *   - Must be applied to <select> element
 *   - Must have exactly 1 existing option (placeholder)
 *   - Placeholder must have empty value=""
 */

import { querySelectorAll } from '@utils';

const US_STATES = [
  { name: 'Alabama', value: 'AL' },
  { name: 'Alaska', value: 'AK' },
  { name: 'Arizona', value: 'AZ' },
  { name: 'Arkansas', value: 'AR' },
  { name: 'California', value: 'CA' },
  { name: 'Colorado', value: 'CO' },
  { name: 'Connecticut', value: 'CT' },
  { name: 'Delaware', value: 'DE' },
  { name: 'Florida', value: 'FL' },
  { name: 'Georgia', value: 'GA' },
  { name: 'Hawaii', value: 'HI' },
  { name: 'Idaho', value: 'ID' },
  { name: 'Illinois', value: 'IL' },
  { name: 'Indiana', value: 'IN' },
  { name: 'Iowa', value: 'IA' },
  { name: 'Kansas', value: 'KS' },
  { name: 'Kentucky', value: 'KY' },
  { name: 'Louisiana', value: 'LA' },
  { name: 'Maine', value: 'ME' },
  { name: 'Maryland', value: 'MD' },
  { name: 'Massachusetts', value: 'MA' },
  { name: 'Michigan', value: 'MI' },
  { name: 'Minnesota', value: 'MN' },
  { name: 'Mississippi', value: 'MS' },
  { name: 'Missouri', value: 'MO' },
  { name: 'Montana', value: 'MT' },
  { name: 'Nebraska', value: 'NE' },
  { name: 'Nevada', value: 'NV' },
  { name: 'New Hampshire', value: 'NH' },
  { name: 'New Jersey', value: 'NJ' },
  { name: 'New Mexico', value: 'NM' },
  { name: 'New York', value: 'NY' },
  { name: 'North Carolina', value: 'NC' },
  { name: 'North Dakota', value: 'ND' },
  { name: 'Ohio', value: 'OH' },
  { name: 'Oklahoma', value: 'OK' },
  { name: 'Oregon', value: 'OR' },
  { name: 'Pennsylvania', value: 'PA' },
  { name: 'Rhode Island', value: 'RI' },
  { name: 'South Carolina', value: 'SC' },
  { name: 'South Dakota', value: 'SD' },
  { name: 'Tennessee', value: 'TN' },
  { name: 'Texas', value: 'TX' },
  { name: 'Utah', value: 'UT' },
  { name: 'Vermont', value: 'VT' },
  { name: 'Virginia', value: 'VA' },
  { name: 'Washington', value: 'WA' },
  { name: 'West Virginia', value: 'WV' },
  { name: 'Wisconsin', value: 'WI' },
  { name: 'Wyoming', value: 'WY' },
];

export function init(config) {
  // Find all select elements with data-hs-form-select="states"
  const stateSelects = querySelectorAll(config, 'states');

  stateSelects.forEach((selectElement) => {
    const select = selectElement as HTMLSelectElement;
    if (select.tagName !== 'SELECT') {
      console.warn(
        '[select-states] data-hs-form-select="states" must be on a <select> element',
        select
      );
      return;
    }

    const existingOptions = select.querySelectorAll('option') as NodeListOf<HTMLOptionElement>;

    // Validate: Must have exactly 1 option (placeholder template)
    if (existingOptions.length !== 1) {
      console.warn(
        '[select-states] Select must have exactly 1 default template option (placeholder). Found:',
        existingOptions.length,
        select
      );
      return;
    }

    const templateOption = existingOptions[0];

    // Validate: Template option must have empty value
    if (templateOption.value !== '') {
      console.warn(
        '[select-states] Template option must have empty value for placeholder. Found value:',
        templateOption.value,
        select
      );
      return;
    }

    // Store placeholder text and clear select
    const placeholderText = templateOption.textContent;
    select.innerHTML = '';

    // Re-create placeholder as first option
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = placeholderText;
    select.appendChild(placeholder);

    // Add all 50 states after placeholder
    US_STATES.forEach((state) => {
      const option = document.createElement('option');
      option.value = state.value;
      option.textContent = state.name;
      select.appendChild(option);
    });
  });

  return {
    result: `states initialized on ${stateSelects.length} select${stateSelects.length !== 1 ? 's' : ''}`,
    destroy: () => {
      // No cleanup needed - states are just populated into native select
    },
  };
}
